import type { LocationInfo } from '@/lib/locationsApi'
import type { ProjectZomboidVersionSelection } from '@/lib/projectZomboidVersions'
import { isMicrosoftAdsEnabled } from '@/lib/microsoftAds'
import { hasMeasurementConsent, readMeasurementConsent } from '@/lib/measurementConsent'

export interface DeploymentSelection {
  game_slug?: string
  template_slug?: string | null
  name?: string
  description?: string | null
  artwork?: {
    icon_url?: string | null
    background_url?: string | null
    logo_key?: string | null
  }
  estimate?: Record<string, unknown> | null
  template?: Record<string, unknown> | null
  service?: { egg_id?: number | null } | null
  package?: PackageSelection | null
  package_terms?: PackagePublicTerms | null
}

export interface PackageSelection {
  package_version_id: number
  package_key: string
  display_name: string
  currency: string
  /** Authoritative compatibility snapshot supplied with the published offer. */
  profile?: { egg_id?: number; [key: string]: unknown }
  pricing: {
    minimum_minor: number
    ceiling_minor: number
    hourly_minor_numerator: number
    hourly_denominator: number
  }
  limits: {
    cpu_millicores: number
    memory_bytes: number
    storage_bytes: number
    backup_bytes: number
    transfer_bytes: number
  }
}

export interface PublicPackageCatalog {
  packages: PackageSelection[]
  terms?: PackagePublicTerms | null
}

export interface PackagePublicTerms {
  trial: { days: number; card_optional: boolean; copy: string }
  billing: { period: string; hourly_denominator: number; taxes: string; minimum_inclusive?: boolean }
  consent: Record<string, unknown>
  recovery?: { days: number; copy: string }
  backups?: { included_in_package: boolean; copy: string }
}

export interface DeploymentIntent {
  intent_uuid: string
  status: string
  expires_at: string
  selection: DeploymentSelection
}

export interface SignupCredit {
  enabled: boolean
  amount_minor: number
  currency: string
  expires_days?: number | null
}

export interface RegistrationAccountFields {
  email: string
  first_name: string
  last_name: string
  password: string
}

export interface RegistrationConfigurationSubmission {
  server_name: string
  environment: Record<string, string>
  project_zomboid_version?: ProjectZomboidVersionSelection
}

export type RegistrationStep = 'email' | 'location' | 'configuration' | 'profile' | 'payment'

export type IntentRestoreFailure = {
  errorCode: 'intent_expired' | 'intent_restore_failed'
  expired: boolean
  retryable: boolean
}

const INTENT_STORAGE_KEY = 'peltast:registration-intent'
const IDEMPOTENCY_PREFIX = 'peltast:registration-idempotency:'
const ATTRIBUTION_KEYS = [
  'campaign',
  'referral',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
] as const
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'msclkid'] as const

function discardUnconsentedClickIds(params: URLSearchParams): void {
  if (hasMeasurementConsent()) return
  for (const key of CLICK_ID_KEYS) params.delete(key)
}

function storage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.sessionStorage } catch { return null }
}

export function restoreIntentUuid(search: string): string | null {
  const params = new URLSearchParams(search)
  const fromUrl = params.get('intent') || params.get('intent_uuid') || params.get('deployment_intent')
  if (fromUrl) {
    storage()?.setItem(INTENT_STORAGE_KEY, fromUrl)
    return fromUrl
  }
  return storage()?.getItem(INTENT_STORAGE_KEY) || null
}

export class UnavailablePackageSelectionError extends Error {
  constructor() {
    super('This package selection is unavailable. Choose a current package to continue.')
    this.name = 'UnavailablePackageSelectionError'
  }
}

export function requestedPackageVersion(search: string): number | null {
  const params = new URLSearchParams(search)
  const game = params.get('game')
  if (!['project-zomboid', 'minecraft'].includes(game || '') && !params.has('package')) return null
  const raw = params.get('package')
  if (!/^[a-z0-9][a-z0-9-]{0,99}$/.test(game || '') || params.getAll('game').length !== 1
      || params.getAll('package').length !== 1 || !raw || !/^[1-9]\d*$/.test(raw)) {
    throw new UnavailablePackageSelectionError()
  }
  const version = Number(raw)
  if (!Number.isSafeInteger(version)) throw new UnavailablePackageSelectionError()
  if (game === 'minecraft' && (params.getAll('template').length !== 1
      || !/^[a-z0-9][a-z0-9-]*$/.test(params.get('template') || ''))) {
    throw new UnavailablePackageSelectionError()
  }
  if (game !== 'minecraft' && params.has('template')) throw new UnavailablePackageSelectionError()
  return version
}

export function assertSelectedPackageIntent(intent: DeploymentIntent, requestedVersion?: number | null): DeploymentIntent {
  if (!isPackageIntent(intent) && requestedVersion == null
      && !['project-zomboid', 'minecraft'].includes(intent.selection?.game_slug || '')) return intent
  const selected = intent.selection.package?.package_version_id
  if (!Number.isSafeInteger(selected) || !selected || selected <= 0
      || (requestedVersion != null && selected !== requestedVersion)) {
    throw new UnavailablePackageSelectionError()
  }
  return intent
}

export function assertCurrentPackageIntent(intent: DeploymentIntent, catalog: PublicPackageCatalog): DeploymentIntent {
  if (!isPackageIntent(intent) && !['project-zomboid', 'minecraft'].includes(intent.selection?.game_slug || '')) return intent
  const selected = intent.selection.package
  const current = catalog.packages?.find(row => row.package_version_id === selected?.package_version_id)
  if (!selected || !current || !catalog.terms || !intent.selection.package_terms
      || current.currency !== selected.currency
      || current.pricing.minimum_minor !== selected.pricing.minimum_minor
      || current.pricing.ceiling_minor !== selected.pricing.ceiling_minor
      || current.profile?.egg_id !== selected.profile?.egg_id
      || catalog.terms?.trial.days !== intent.selection.package_terms?.trial.days
      || catalog.terms?.trial.card_optional !== intent.selection.package_terms?.trial.card_optional
      || catalog.terms?.trial.copy !== intent.selection.package_terms?.trial.copy
      || catalog.terms?.recovery?.days !== intent.selection.package_terms?.recovery?.days
      || catalog.terms?.recovery?.copy !== intent.selection.package_terms?.recovery?.copy
      || catalog.terms?.backups?.included_in_package !== intent.selection.package_terms?.backups?.included_in_package
      || catalog.terms?.backups?.copy !== intent.selection.package_terms?.backups?.copy) {
    throw new UnavailablePackageSelectionError()
  }
  return intent
}

export function packageIntentPayload(
  search: string,
  originalPage?: string,
): Record<string, unknown> | null {
  const params = new URLSearchParams(search)
  const packageVersionId = requestedPackageVersion(search)
  if (packageVersionId === null) return null
  discardUnconsentedClickIds(params)

  const attribution = Object.fromEntries(
    ATTRIBUTION_KEYS
      .map(key => [key, params.get(key)] as const)
      .filter((entry): entry is [typeof ATTRIBUTION_KEYS[number], string] => (
        Boolean(entry[1]) && (entry[0] !== 'msclkid' || isMicrosoftAdsEnabled())
      )),
  )

  let safeOriginalPage: string | null = null
  try {
    const parsed = originalPage ? new URL(originalPage) : null
    if (parsed && ['http:', 'https:'].includes(parsed.protocol)) {
      discardUnconsentedClickIds(parsed.searchParams)
      safeOriginalPage = parsed.toString()
    }
  } catch {
    safeOriginalPage = null
  }

  return {
    game_slug: params.get('game'),
    ...(params.get('game') === 'minecraft' ? { template_slug: params.get('template') } : {}),
    package_version_id: packageVersionId,
    advertising_consent: readMeasurementConsent(),
    ...(safeOriginalPage ? { original_page: safeOriginalPage } : {}),
    ...(Object.keys(attribution).length ? { attribution } : {}),
  }
}

export function intentSearchFromLegacyQuery(search: string, intentUuid: string): string {
  const params = new URLSearchParams(search)
  discardUnconsentedClickIds(params)
  params.delete('game')
  params.delete('template')
  params.delete('package')
  params.delete('intent_uuid')
  params.delete('deployment_intent')
  params.set('intent', intentUuid)
  return `?${params.toString()}`
}

export function clearRegistrationIntent(): void {
  storage()?.removeItem(INTENT_STORAGE_KEY)
}

export function intentSearchWithoutStoredIntent(search: string): string {
  const params = new URLSearchParams(search)
  discardUnconsentedClickIds(params)
  params.delete('intent')
  params.delete('intent_uuid')
  params.delete('deployment_intent')
  const remaining = params.toString()
  return remaining ? `?${remaining}` : ''
}

export function attributedRegistrationRestartUrl(
  baseUrl: string,
  search: string,
  currentHostname = typeof window === 'undefined' ? '' : window.location.hostname,
): string {
  const restart = new URL(baseUrl)
  discardUnconsentedClickIds(restart.searchParams)
  if (currentHostname === 'console.metrly.localhost'
      && restart.hostname === 'www.metrly.com') {
    restart.hostname = 'www.metrly.localhost'
  }
  const source = new URLSearchParams(search)
  discardUnconsentedClickIds(source)
  for (const key of ATTRIBUTION_KEYS) {
    if (key === 'msclkid' && !isMicrosoftAdsEnabled()) continue
    const value = source.get(key)
    if (value) restart.searchParams.set(key, value)
  }
  restart.hash = ''
  return restart.toString()
}

/** Send privacy choices to Forum's public page, including on local dev. */
export function forumPrivacyChoicesUrl(
  currentHostname = typeof window === 'undefined' ? '' : window.location.hostname,
): string {
  const forum = new URL(attributedRegistrationRestartUrl(
    'https://www.metrly.com/?privacy=1', '', currentHostname,
  ))
  const savedIntent = storage()?.getItem(INTENT_STORAGE_KEY)
  if (savedIntent && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(savedIntent)
      && forum.protocol === 'https:'
      && (forum.hostname === 'www.metrly.com' || forum.hostname === 'www.metrly.localhost')) {
    forum.searchParams.set('privacy_receipt', savedIntent)
  }
  return forum.toString()
}

export function clearExpiredRegistrationIntent(pathname: string, search: string, hash = ''): void {
  clearRegistrationIntent()
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${intentSearchWithoutStoredIntent(search)}${hash}`,
  )
}

export function registrationIdempotencyKey(intentUuid: string): string {
  const key = `${IDEMPOTENCY_PREFIX}${intentUuid}`
  const existing = storage()?.getItem(key)
  if (existing) return existing
  const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  storage()?.setItem(key, generated)
  return generated
}

export function availableStandardLocations(locations: LocationInfo[]): LocationInfo[] {
  return locations.filter(location => location.tier_availability?.standard === true)
}

export function isProjectZomboidIntent(intent: DeploymentIntent | null): boolean {
  return intent?.selection?.game_slug === 'project-zomboid'
}

export function isPackageIntent(intent: DeploymentIntent | null): boolean {
  return Boolean(intent?.selection?.package?.package_version_id)
}

export function formatPackageMoney(minor: number, currency = 'usd'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(minor / 100)
}

export function formatPackageHeadlineMoney(minor: number, currency = 'usd'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minor / 100)
}

export function registrationStepOrder(intent: DeploymentIntent | null): RegistrationStep[] {
  return isPackageIntent(intent) || isProjectZomboidIntent(intent) || intent?.selection.game_slug === 'minecraft'
    ? ['configuration', 'email', 'profile', 'payment']
    : ['email', 'profile', 'payment']
}

export function formatAuthoritativeEstimate(estimate: Record<string, unknown> | null | undefined): string | null {
  if (!estimate) return null
  const currency = String(estimate.currency || 'usd').toUpperCase()
  if (typeof estimate.monthly_minor === 'number') {
    const rounded = Math.max(1, Math.round(estimate.monthly_minor / 100))
    return `About ${new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(rounded)}/month at typical usage`
  }
  if (typeof estimate.monthly_usd === 'number') {
    const rounded = Math.max(1, Math.round(estimate.monthly_usd))
    return `About ${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(rounded)}/month at typical usage`
  }
  if (typeof estimate.display === 'string' && estimate.display.trim()) return estimate.display
  return null
}

export function formatSignupCredit(credit: SignupCredit | null): string | null {
  if (!credit?.enabled || credit.amount_minor <= 0) return null
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: credit.currency || 'usd',
  }).format(credit.amount_minor / 100)
}

export class UnavailableDeploymentIntentError extends Error {
  constructor() {
    super('This deployment selection has expired or was cancelled. Return to the game page and choose it again.')
    this.name = 'UnavailableDeploymentIntentError'
  }
}

export function classifyIntentRestoreFailure(error: unknown, initialIntentRetrieval = false): IntentRestoreFailure {
  const status = typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status?: unknown }).status)
    : 0
  const expired = error instanceof UnavailableDeploymentIntentError
    || status === 410
    || (initialIntentRetrieval && status === 404)
  return {
    errorCode: expired ? 'intent_expired' : 'intent_restore_failed',
    expired,
    retryable: !expired,
  }
}

export function assertUsableIntent(intent: DeploymentIntent, now = Date.now()): DeploymentIntent {
  if (intent.status === 'expired' || intent.status === 'cancelled' || Date.parse(intent.expires_at) <= now) {
    throw new UnavailableDeploymentIntentError()
  }
  return intent
}

export function registrationEndpoint(intent: DeploymentIntent | null): string {
  return intent ? '/registrations' : '/users/create'
}

export function buildRegistrationPayload(
  account: RegistrationAccountFields,
  paymentMethodId: string | null,
  intent: DeploymentIntent | null,
  locationId?: string,
  configuration?: RegistrationConfigurationSubmission,
  setupIntentId?: string | null,
): Record<string, unknown> {
  return {
    email: account.email,
    first_name: account.first_name,
    last_name: account.last_name,
    ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
    ...(setupIntentId ? { setup_intent_id: setupIntentId } : {}),
    password: account.password,
    password_confirmation: account.password,
    ...(intent ? { intent_uuid: intent.intent_uuid } : {}),
    ...(isPackageIntent(intent) || isProjectZomboidIntent(intent) || intent?.selection.game_slug === 'minecraft' ? {
      location_id: Number(locationId),
      server_name: configuration?.server_name,
      environment: configuration?.environment,
      ...(isProjectZomboidIntent(intent) ? { project_zomboid_version: configuration?.project_zomboid_version } : {}),
    } : {}),
  }
}

export function stripeSetupErrorMessage(error: unknown): string | null {
  return error ? 'Payment verification failed. Please check your card details.' : null
}
